import React, {useEffect, useRef} from 'react';
import AppSpinner from "../AppSpinner.tsx";
import {Flex} from "@chakra-ui/react";
import {useProjectsStore} from "../../store/useProjectsStore.ts";

interface MainTableActionsProps {
  selectedRows: string[];
  setSelectedRows: (selectedRows: string[]) => void
  isDownloading: Record<string, boolean>;
  setIsDownloading: (update: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void;
  selectAllCompletedProjects: () => void;
  onCreateNewProjectClicked: () => Promise<void>;
}

const MainTableActions: React.FC<MainTableActionsProps> = (
  {
    selectedRows,
    setSelectedRows,
    isDownloading,
    setIsDownloading,
    selectAllCompletedProjects,
    onCreateNewProjectClicked,
  }: MainTableActionsProps) => {
  const selectedRowsRef = useRef<string[]>(selectedRows);
  const {projectsToView, clearProjectsToView} = useProjectsStore();

  const {bulkDownload} = useProjectsStore()

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedRowsRef.current.length > 0) {
        setSelectedRows([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setSelectedRows]);


  const clearSelectedRows = (): void => {
    setSelectedRows([]);
  }

  const onBulkDownloadButtonClick = async (): Promise<void> => {
    setIsDownloading((prevState: Record<string, boolean>): Record<string, boolean> => {
      const updated = {...prevState};
      updated["bulkDownload"] = true;
      return updated;
    })

    await bulkDownload(selectedRows);
    setIsDownloading((prevState: Record<string, boolean>): Record<string, boolean> => {
      const updated = {...prevState};
      updated["bulkDownload"] = false;
      return updated;
    })
    clearSelectedRows()
  }

  return (
    <Flex className="mb-1 items-center justify-between">
      <Flex className=" items-center gap-2">
        {projectsToView.length > 0 && <button
            className={`w-[150px] h-[35px] bg-brandPurple-500 rounded-lg font-semibold justify-center relative active:scale-95 transition-transform duration-100 text-sm`}
            onClick={clearProjectsToView}
        >
            Clear Table
        </button>}
        <button
          className={`w-[150px] h-[35px] bg-brandPurple-500 rounded-lg font-semibold justify-center relative active:scale-95 transition-transform duration-100 ${
            selectedRows.length > 0 ? 'visible' : 'invisible'
          } text-sm`}
          onClick={() => !isDownloading["bulkDownload"] && onBulkDownloadButtonClick()}
        >
          {isDownloading["bulkDownload"]
            ? <AppSpinner size={"md"}/>
            : "Download Selected"
          }
        </button>
        <button
          className={`w-[80px] h-[35px] bg-brandPurple-500 rounded-lg font-semibold justify-center relative active:scale-95 transition-transform duration-100 ${
            selectedRows.length > 0 ? 'visible' : 'invisible'
          } text-sm`}
          onClick={selectAllCompletedProjects}
        >
          Select All
        </button>
        <button
          className={`w-[140px] h-[35px] bg-brandPurple-500 p-2 rounded-lg font-semibold justify-center relative active:scale-95 transition-transform duration-100 ${
            selectedRows.length > 0 ? 'visible' : 'invisible'
          } text-sm`}
          onClick={clearSelectedRows}
        >
          Remove Selected
        </button>
      </Flex>
      <Flex className="items-center gap-2">
        <Flex
          className="bg-brandPurple-500 py-1 px-3 rounded-lg font-semibold text-sm justify-center cursor-pointer hover:bg-violetPurple-500"
          onClick={onCreateNewProjectClicked}
        >
          New Project
        </Flex>
      </Flex>
    </Flex>
  )
}

export default MainTableActions;